"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";
import { DataTable, type Column } from "./DataTable";
import { CSVExportButton, PrintButton, type PrintData } from "./PrintExport";
import { useERP, formatBDT, formatNum, type ID, type ERPState } from "@/lib/erp/store";

export type GenericEntityType = "product" | "customer" | "supplier" | "warehouse" | "budget" | "pricing" | "marketing" | "portal" | "payment" | "support" | "document" | "api" | "mobile" | "quality" | "industry";

export interface GenericListPageProps {
  entityType: GenericEntityType;
  onNavigate?: (pageId: string) => void;
  title?: string;
  description?: string;
}

interface RowData { id: ID; code?: string; name: string; sub1?: string; sub2?: string; sub3?: string; amount?: number; status?: string; [k: string]: unknown; }

export function GenericListPage({ entityType, onNavigate, title, description }: GenericListPageProps) {
  const erp = useERP();

  const { rows, columns, statCards, formPageId, listPageLabel } = React.useMemo(() => {
    return computeConfig(entityType, erp);
  }, [entityType, erp]);

  const handlePrint = () => {
    const data: PrintData = {
      title: title || listPageLabel,
      docNumber: `RPT-${Date.now().toString().slice(-6)}`,
      docDate: new Date().toISOString().slice(0, 10),
      columns: columns.filter(c => c.key !== "actions").map(c => ({ key: c.key, label: c.header, align: c.align })),
      rows: rows.map(r => {
        const out: Record<string, string | number> = {};
        for (const c of columns) if (c.key !== "actions") out[c.key] = String((r as Record<string, unknown>)[c.key] ?? "");
        return out;
      }),
    };
    return data;
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">{title || listPageLabel}</h1>
          {description && <p className="text-muted-foreground mt-1 text-sm">{description}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <PrintButton data={handlePrint()} label="Print" />
          <CSVExportButton columns={columns.filter(c => c.key !== "actions").map(c => ({ key: c.key, label: c.header }))} rows={rows} filename={`${entityType}-${Date.now()}.csv`} />
          {formPageId && onNavigate && (
            <Button size="sm" onClick={() => onNavigate(formPageId)}><Plus className="size-4" /> New</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((s, i) => (
          <Card key={i}>
            <CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">{s.label}</CardTitle></CardHeader>
            <CardContent><div className={`text-2xl font-semibold ${s.tone || ""}`}>{s.value}</div></CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle>{listPageLabel} List</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={rows}
            rowKey={r => r.id}
            searchKeys={["code", "name", "sub1", "sub2", "status"]}
            onRowClick={() => {}}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function computeConfig(entityType: GenericEntityType, erp: ERPState) {
  const baseActions = (onEdit?: () => void, onView?: () => void): Column<RowData> => ({
    key: "actions", header: "Actions", align: "right",
    render: () => (
      <div className="flex justify-end gap-1">
        {onView && <Button size="icon" variant="ghost" className="size-7" onClick={(e) => { e.stopPropagation(); onView(); }}><Eye className="size-3.5" /></Button>}
        {onEdit && <Button size="icon" variant="ghost" className="size-7" onClick={(e) => { e.stopPropagation(); onEdit(); }}><Pencil className="size-3.5" /></Button>}
        <Button size="icon" variant="ghost" className="size-7 text-destructive" onClick={(e) => { e.stopPropagation(); }}><Trash2 className="size-3.5" /></Button>
      </div>
    ),
  });

  if (entityType === "product") {
    return {
      rows: erp.products as unknown as RowData[],
      columns: [
        { key: "sku", header: "SKU", sortable: true, render: r => <span className="font-mono text-xs">{(r as Record<string, unknown>).sku as string}</span> },
        { key: "name", header: "Product Name", sortable: true, render: r => <span className="font-medium">{r.name}</span> },
        { key: "category", header: "Category", render: r => String((r as Record<string, unknown>).category ?? "") },
        { key: "brand", header: "Brand", render: r => String((r as Record<string, unknown>).brand ?? "") },
        { key: "costPrice", header: "Cost", align: "right", render: r => formatBDT(Number((r as Record<string, unknown>).costPrice ?? 0)) },
        { key: "salePrice", header: "Sale", align: "right", render: r => formatBDT(Number((r as Record<string, unknown>).salePrice ?? 0)) },
        { key: "status", header: "Status", render: r => <Badge variant={String((r as Record<string, unknown>).status) === "Active" ? "default" : "secondary"}>{String((r as Record<string, unknown>).status)}</Badge> },
        baseActions(),
      ] as Column<RowData>[],
      statCards: [
        { label: "Total Products", value: formatNum(erp.products.length) },
        { label: "Active SKUs", value: formatNum(erp.products.filter(p => p.status === "Active").length) },
        { label: "Categories", value: formatNum(new Set(erp.products.map(p => p.category)).size) },
        { label: "Avg Margin", value: `${(erp.products.reduce((s, p) => s + ((p.salePrice - p.costPrice) / p.costPrice) * 100, 0) / Math.max(1, erp.products.length)).toFixed(1)}%` },
      ],
      formPageId: "md-products-new" as const,
      listPageLabel: "Products",
    };
  }
  if (entityType === "customer") {
    return {
      rows: erp.customers as unknown as RowData[],
      columns: [
        { key: "code", header: "Code", sortable: true, render: r => <span className="font-mono text-xs">{(r as Record<string, unknown>).code as string}</span> },
        { key: "name", header: "Name", sortable: true, render: r => <span className="font-medium">{r.name}</span> },
        { key: "type", header: "Type", render: r => String((r as Record<string, unknown>).type ?? "") },
        { key: "zone", header: "Zone", render: r => String((r as Record<string, unknown>).zone ?? "") },
        { key: "creditLimit", header: "Credit Limit", align: "right", render: r => formatBDT(Number((r as Record<string, unknown>).creditLimit ?? 0)) },
        { key: "outstanding", header: "Outstanding", align: "right", render: r => <span className={Number((r as Record<string, unknown>).outstanding) > 0 ? "text-destructive font-semibold" : ""}>{formatBDT(Number((r as Record<string, unknown>).outstanding ?? 0))}</span> },
        { key: "status", header: "Status", render: r => { const s = String((r as Record<string, unknown>).status); return <Badge variant={s === "Active" ? "default" : s === "Blocked" ? "destructive" : "secondary"}>{s}</Badge>; } },
        baseActions(),
      ] as Column<RowData>[],
      statCards: [
        { label: "Total Customers", value: formatNum(erp.customers.length) },
        { label: "Distributors", value: formatNum(erp.customers.filter(c => c.type === "Distributor").length) },
        { label: "Blocked", value: formatNum(erp.customers.filter(c => c.status === "Blocked").length), tone: "text-destructive" },
        { label: "Total Outstanding", value: formatBDT(erp.customers.reduce((s, c) => s + c.outstanding, 0)) },
      ],
      formPageId: "md-customers-new" as const,
      listPageLabel: "Customers",
    };
  }
  if (entityType === "supplier") {
    return {
      rows: erp.suppliers as unknown as RowData[],
      columns: [
        { key: "code", header: "Code", sortable: true, render: r => <span className="font-mono text-xs">{(r as Record<string, unknown>).code as string}</span> },
        { key: "name", header: "Supplier", sortable: true, render: r => <span className="font-medium">{r.name}</span> },
        { key: "category", header: "Category", render: r => String((r as Record<string, unknown>).category ?? "") },
        { key: "contactPerson", header: "Contact", render: r => String((r as Record<string, unknown>).contactPerson ?? "") },
        { key: "leadTimeDays", header: "Lead Time", align: "right", render: r => `${(r as Record<string, unknown>).leadTimeDays}d` },
        { key: "rating", header: "Rating", align: "right", render: r => `★ ${Number((r as Record<string, unknown>).rating ?? 0).toFixed(1)}` },
        { key: "status", header: "Status", render: r => <Badge variant={String((r as Record<string, unknown>).status) === "Active" ? "default" : "secondary"}>{String((r as Record<string, unknown>).status)}</Badge> },
        baseActions(),
      ] as Column<RowData>[],
      statCards: [
        { label: "Suppliers", value: formatNum(erp.suppliers.length) },
        { label: "Active", value: formatNum(erp.suppliers.filter(s => s.status === "Active").length) },
        { label: "Avg Rating", value: `★ ${(erp.suppliers.reduce((s, x) => s + x.rating, 0) / Math.max(1, erp.suppliers.length)).toFixed(2)}` },
        { label: "Avg Lead Time", value: `${Math.round(erp.suppliers.reduce((s, x) => s + x.leadTimeDays, 0) / Math.max(1, erp.suppliers.length))}d` },
      ],
      formPageId: "md-suppliers-new" as const,
      listPageLabel: "Suppliers",
    };
  }
  if (entityType === "warehouse") {
    return {
      rows: erp.warehouses as unknown as RowData[],
      columns: [
        { key: "code", header: "Code", sortable: true, render: r => <span className="font-mono text-xs">{(r as Record<string, unknown>).code as string}</span> },
        { key: "name", header: "Warehouse", sortable: true, render: r => <span className="font-medium">{r.name}</span> },
        { key: "type", header: "Type", render: r => String((r as Record<string, unknown>).type ?? "") },
        { key: "branch", header: "Branch", render: r => String((r as Record<string, unknown>).branch ?? "") },
        { key: "capacity", header: "Capacity", align: "right", render: r => formatNum(Number((r as Record<string, unknown>).capacity ?? 0)) },
        { key: "utilization", header: "Utilization", align: "right", render: r => { const u = Number((r as Record<string, unknown>).utilization ?? 0); return <span className={u > 80 ? "text-destructive font-semibold" : ""}>{u}%</span>; } },
        { key: "status", header: "Status", render: r => <Badge variant={String((r as Record<string, unknown>).status) === "Active" ? "default" : "secondary"}>{String((r as Record<string, unknown>).status)}</Badge> },
        baseActions(),
      ] as Column<RowData>[],
      statCards: [
        { label: "Warehouses", value: formatNum(erp.warehouses.length) },
        { label: "Main Sites", value: formatNum(erp.warehouses.filter(w => w.type === "Main").length) },
        { label: "Avg Utilization", value: `${Math.round(erp.warehouses.reduce((s, w) => s + w.utilization, 0) / Math.max(1, erp.warehouses.length))}%` },
        { label: "Total Capacity", value: formatNum(erp.warehouses.reduce((s, w) => s + w.capacity, 0)) },
      ],
      formPageId: "md-warehouses-new" as const,
      listPageLabel: "Warehouses",
    };
  }
  // Generic fallback for budget/pricing/marketing/portal/payment/support/document/api/mobile/quality/industry
  const labelMap: Record<string, { label: string; sample: { code: string; name: string; sub1: string; sub2: string; amount: number; status: string }[] }> = {
    budget: { label: "Budget vs Actual", sample: [
      { code: "BUD-FY26-SAL", name: "Sales Budget", sub1: "FY 2026", sub2: "Annual", amount: 85000000, status: "On Track" },
      { code: "BUD-FY26-MKT", name: "Marketing Budget", sub1: "FY 2026", sub2: "Q2", amount: 12500000, status: "Over" },
      { code: "BUD-FY26-LOG", name: "Logistics Budget", sub1: "FY 2026", sub2: "Annual", amount: 22000000, status: "Under" },
    ] },
    pricing: { label: "Pricing Rules", sample: [
      { code: "PR-DIST-01", name: "Distributor Discount Tier 1", sub1: "Distributor", sub2: "5% off MRP", amount: 5, status: "Active" },
      { code: "PR-RETL-02", name: "Retailer Discount Tier 2", sub1: "Retailer", sub2: "3% off MRP", amount: 3, status: "Active" },
    ] },
    marketing: { label: "Marketing Campaigns", sample: [
      { code: "CAMP-EID26", name: "Eid Festival Campaign", sub1: "All Zones", sub2: "Mixed Media", amount: 1800000, status: "Live" },
      { code: "CAMP-WIN26", name: "Winter Warmth Promo", sub1: "Northern Zones", sub2: "Digital", amount: 950000, status: "Planned" },
    ] },
    portal: { label: "B2B Portal Orders", sample: [
      { code: "PO-PORT-8842", name: "Rahim Distributors - Bulk", sub1: "Portal", sub2: "Pending Confirmation", amount: 480000, status: "Pending" },
    ] },
    payment: { label: "Collection Register", sample: erp.customerReceipts.map(r => ({
      code: r.receiptNumber, name: erp.getCustomer(r.customerId)?.name ?? "—", sub1: r.paymentMode, sub2: r.bankRef ?? "—", amount: r.amount, status: r.status,
    })) },
    support: { label: "Support Tickets", sample: [
      { code: "TKT-1001", name: "Invoice discrepancy - CUS-0001", sub1: "Billing", sub2: "High", amount: 0, status: "Open" },
      { code: "TKT-1002", name: "PO delivery delay", sub1: "Logistics", sub2: "Medium", amount: 0, status: "In Progress" },
    ] },
    document: { label: "Document Repository", sample: [
      { code: "DOC-VAT-26", name: "VAT Return FY2026 Q2", sub1: "Finance", sub2: "PDF", amount: 0, status: "Filed" },
      { code: "DOC-ISO-22", name: "ISO 9001 Certificate", sub1: "Quality", sub2: "PDF", amount: 0, status: "Active" },
    ] },
    api: { label: "API Catalogue", sample: [
      { code: "API-001", name: "Get Stock Position", sub1: "GET", sub2: "/api/stock", amount: 0, status: "Production" },
      { code: "API-002", name: "Create Sales Order", sub1: "POST", sub2: "/api/orders", amount: 0, status: "Production" },
    ] },
    mobile: { label: "Mobile Devices", sample: [
      { code: "MOB-SFA-01", name: "Samsung A14 - Salesman", sub1: "Sadia Islam", sub2: "Sylhet", amount: 0, status: "Active" },
      { code: "MOB-SFA-02", name: "Redmi 12 - Driver", sub1: "Rakib Hossain", sub2: "Dhaka", amount: 0, status: "Active" },
    ] },
    quality: { label: "Quality Inspections", sample: [
      { code: "QC-442", name: "GRN-2026-442 Inspection", sub1: "ACI Foods", sub2: "Pass", amount: 0, status: "Passed" },
    ] },
    industry: { label: "Industry Modules", sample: [
      { code: "POS-RETAIL", name: "Retail POS Module", sub1: "Distribution", sub2: "On", amount: 0, status: "Active" },
      { code: "ECOM-WEB", name: "E-Commerce Module", sub1: "Distribution", sub2: "On", amount: 0, status: "Active" },
      { code: "MFG-PROD", name: "Manufacturing Module", sub1: "Industrial", sub2: "Off", amount: 0, status: "Inactive" },
      { code: "3PL-OPS", name: "3PL Management", sub1: "Logistics", sub2: "Off", amount: 0, status: "Inactive" },
    ] },
  };
  const cfg = labelMap[entityType] || { label: "Records", sample: [] };
  return {
    rows: cfg.sample as RowData[],
    columns: [
      { key: "code", header: "Code", sortable: true, render: r => <span className="font-mono text-xs">{r.code}</span> },
      { key: "name", header: "Name / Description", sortable: true, render: r => <span className="font-medium">{r.name}</span> },
      { key: "sub1", header: "Category" },
      { key: "sub2", header: "Detail" },
      { key: "amount", header: "Amount", align: "right", render: r => r.amount ? formatBDT(r.amount) : "—" },
      { key: "status", header: "Status", render: r => <Badge variant={r.status === "Active" || r.status === "Passed" || r.status === "Live" || r.status === "Production" || r.status === "On Track" || r.status === "Filed" ? "default" : r.status === "Over" || r.status === "Open" ? "destructive" : "secondary"}>{r.status}</Badge> },
      baseActions(),
    ] as Column<RowData>[],
    statCards: [
      { label: "Total Records", value: formatNum(cfg.sample.length) },
      { label: "Active", value: formatNum(cfg.sample.filter(s => ["Active", "Live", "Production", "On Track", "Filed", "Passed", "Reconciled", "Pending"].includes(s.status)).length) },
      { label: "Open Issues", value: formatNum(cfg.sample.filter(s => ["Open", "Over", "Pending"].includes(s.status)).length), tone: "text-destructive" },
      { label: "Total Value", value: formatBDT(cfg.sample.reduce((s, x) => s + x.amount, 0)) },
    ],
    formPageId: undefined,
    listPageLabel: cfg.label,
  };
}
